/**
 * InlineDiff — Word-level inline diff highlighting between two texts.
 *
 * Renders removed words in red (strikethrough) and added words in green.
 * Uses a simple longest-common-subsequence algorithm on word tokens.
 */

import React, { useMemo } from 'react';

interface InlineDiffProps {
  /** The original text (before modification). */
  original: string;
  /** The modified text (after modification). */
  modified: string;
}

type DiffSegment = {
  type: 'equal' | 'removed' | 'added';
  text: string;
};

/** Tokenize text into words preserving whitespace boundaries. */
const tokenize = (text: string): string[] => {
  return text.match(/\S+|\s+/g) || [];
};

/**
 * Compute longest common subsequence lengths table.
 * Returns a 2D array where lcs[i][j] = LCS length of a[0..i-1] and b[0..j-1].
 */
const lcsTable = (a: string[], b: string[]): number[][] => {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
};

/** Build diff segments from two token arrays using LCS backtracking. */
const buildDiff = (original: string[], modified: string[]): DiffSegment[] => {
  const dp = lcsTable(original, modified);
  const segments: DiffSegment[] = [];
  let i = original.length;
  let j = modified.length;

  // Backtrack
  const stack: DiffSegment[] = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && original[i - 1] === modified[j - 1]) {
      stack.push({ type: 'equal', text: original[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'added', text: modified[j - 1] });
      j--;
    } else {
      stack.push({ type: 'removed', text: original[i - 1] });
      i--;
    }
  }

  // Reverse (we backtracked) and merge consecutive segments of same type
  stack.reverse();
  for (const seg of stack) {
    const last = segments[segments.length - 1];
    if (last && last.type === seg.type) {
      last.text += seg.text;
    } else {
      segments.push({ ...seg });
    }
  }

  return segments;
};

const InlineDiff: React.FC<InlineDiffProps> = ({ original, modified }) => {
  const segments = useMemo(() => {
    const origTokens = tokenize(original);
    const modTokens = tokenize(modified);
    return buildDiff(origTokens, modTokens);
  }, [original, modified]);

  return (
    <div className="text-sm leading-relaxed">
      {segments.map((seg, idx) => {
        if (seg.type === 'removed') {
          return (
            <span
              key={idx}
              className="bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 line-through"
            >
              {seg.text}
            </span>
          );
        }
        if (seg.type === 'added') {
          return (
            <span
              key={idx}
              className="bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300"
            >
              {seg.text}
            </span>
          );
        }
        return (
          <span key={idx} className="text-gray-700 dark:text-gray-300">
            {seg.text}
          </span>
        );
      })}
    </div>
  );
};

export default InlineDiff;
